import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import { Close } from '@mui/icons-material';

import PropertiesView from './PropertiesView';
import SafeJsonView from './SafeJsonView';
import PlanetDetails from './PlanetDetails';
import GalaxyDetails from './GalaxyDetails';
import StarDetails from './StarDetails';

const EntityDetailsModal = ({ 
  open, 
  onClose, 
  entity, 
  entityType,
  additionalData = {} 
}) => {
  const [activeTab, setActiveTab] = React.useState(0);

  if (!open || !entity) return null;

  const getEntityTitle = () => {
    const name = entity.name || entity.planet_id || entity.galaxy_id || entity.star_id || 'Unnamed';
    const typeMap = {
      planet: '🪐 Planet',
      galaxy: '🌌 Galaxy', 
      star: '⭐ Star',
      race: '👽 Race',
      faction: '⚔️ Faction'
    };
    
    return `${typeMap[entityType] || 'Entity'} ${name}`;
  };

  const getEntityDetailsComponent = () => {
    switch (entityType) {
      case 'planet':
        return (
          <PlanetDetails 
            planet={entity} 
            races={additionalData.races || []}
            factions={additionalData.factions || []}
          />
        );
      case 'galaxy':
        return (
          <GalaxyDetails 
            galaxy={entity}
            planets={additionalData.planets || []}
            races={additionalData.races || []}
            factions={additionalData.factions || []}
          />
        );
      case 'star':
        return (
          <StarDetails 
            star={entity}
            planets={additionalData.planets || []}
            galaxies={additionalData.galaxies || []}
          />
        );
      default:
        return <PropertiesView data={entity} />;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            {getEntityTitle()}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={entityType} 
            size="small" 
            color="primary"
            variant="outlined"
          />
          {entity.type && (
            <Chip 
              label={entity.type} 
              size="small" 
              variant="outlined"
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs 
          value={activeTab} 
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 2 }}
        >
          <Tab label="Details" />
          <Tab label="Properties" />
          <Tab label="JSON Data" />
        </Tabs>

        {activeTab === 0 && getEntityDetailsComponent()}
        {activeTab === 1 && <PropertiesView data={entity} />}
        {activeTab === 2 && <SafeJsonView data={entity} />}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntityDetailsModal;